import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Shield, Lock, Eye, EyeOff, CheckCircle2, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('resetToken') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [tokenMissing, setTokenMissing] = useState(false);

  useEffect(() => {
    if (!token) setTokenMissing(true);
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        newPassword,
      });
      setDone(true);
      toast.success('Password reset successful. Please login.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reset failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl relative z-10" data-testid="admin-reset-card">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-primary to-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Shield className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Reset Admin Password</h1>
          <p className="text-sm text-gray-400 mt-2">Enter your new password below.</p>
        </div>

        {tokenMissing && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm flex items-start" data-testid="admin-reset-token-missing">
            <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              Reset token is missing or invalid. Please request a new password reset from the admin login page.
              <div className="mt-3">
                <Button
                  type="button"
                  onClick={() => navigate('/admin-login')}
                  className="bg-primary text-white"
                  data-testid="admin-reset-back-login-invalid"
                >
                  Back to Admin Login
                </Button>
              </div>
            </div>
          </div>
        )}

        {!tokenMissing && !done && (
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="admin-reset-form">
            <div className="space-y-2">
              <Label htmlFor="newPassword" className="text-gray-700 font-medium">
                <Lock className="w-4 h-4 inline-block mr-2 text-gray-400" />
                New Password
              </Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  placeholder="At least 8 characters"
                  className="h-12 bg-gray-50 border-gray-200 focus:bg-white transition-colors pr-12"
                  data-testid="admin-reset-new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-gray-700 font-medium">
                <Lock className="w-4 h-4 inline-block mr-2 text-gray-400" />
                Confirm Password
              </Label>
              <Input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                placeholder="Re-enter password"
                className="h-12 bg-gray-50 border-gray-200 focus:bg-white transition-colors"
                data-testid="admin-reset-confirm-password"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-gradient-to-r from-primary to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all"
              disabled={loading}
              data-testid="admin-reset-submit"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>
        )}

        {done && (
          <div className="space-y-5 text-center" data-testid="admin-reset-success">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm flex items-start">
              <CheckCircle2 className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
              <div>Password reset successful. You can now login with your new password.</div>
            </div>
            <Button
              type="button"
              onClick={() => navigate('/admin-login')}
              className="w-full h-12 bg-gradient-to-r from-primary to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all"
              data-testid="admin-reset-goto-login"
            >
              Go to Admin Login
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default AdminResetPassword;
